import { ApolloLink, type DefaultContext } from "@apollo/client";
import ApolloLinkTimeout from "apollo-link-timeout";
import { createUploadLink } from "apollo-upload-client";

import { type useNetworkMetrics } from "../../analytics/hooks/useNetworkMetrics";
import { NetworkMetricsNames } from "../../analytics/network-metrics";
import { BUILD_TYPE, isPreProd, isProd } from "../../shared/constants";
import { PRE_PROD_TAG, SNAP_ROUTE_TAG, Services, urlMap } from "../../shared/infrastructure";

const uri = urlMap[Services.MY_LENSES_GRAPHQL][BUILD_TYPE];

export const HTTP_STATUS_CODES = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    REQUEST_TIMEOUT: 408,
};

export const hasResponse = (context: DefaultContext): context is DefaultContext & { response: Response } => {
    return !!context["response"];
};

/**
 * Creates the base Apollo links shared by AppApolloProvider and EmbedApolloProvider.
 * Does NOT include the error link or retry link — those differ per provider.
 */
export const createBaseLinks = (
    metrics: ReturnType<typeof useNetworkMetrics>,
): {
    uploadLink: ReturnType<typeof createUploadLink>;
    timeoutLink: ApolloLinkTimeout;
    networkMetricsLink: ApolloLink;
} => {
    const { reportMetric, createLatencyMetric, reportLatencyMetric } = metrics;

    const uploadLink = createUploadLink({
        uri,
        ...(isPreProd() && { headers: { [SNAP_ROUTE_TAG]: PRE_PROD_TAG } }),
        ...(!isProd() && { credentials: "include" }),
    });

    const timeoutLink = new ApolloLinkTimeout(60_000, HTTP_STATUS_CODES.REQUEST_TIMEOUT);

    const networkMetricsLink = new ApolloLink((operation, forward) => {
        const latencyMetric = createLatencyMetric();
        latencyMetric.startTimer();

        return forward(operation).map((result) => {
            const context = operation.getContext();
            const operationName = operation.operationName;
            const statusCode = hasResponse(context) ? context.response.status : 0;

            reportLatencyMetric(latencyMetric, {
                label: operationName,
                statusCode,
                metricName: NetworkMetricsNames.APOLLO_API_LATENCY,
            });
            reportMetric({
                label: operationName,
                statusCode,
                metricName: NetworkMetricsNames.APOLLO_API_REQUEST_COUNTER,
            });
            return result;
        });
    });

    return { uploadLink, timeoutLink, networkMetricsLink };
};
